import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, TrackByFunction, ViewChild } from '@angular/core';
import { FormlyType } from '../ga-formly.service';
import { ATTR_REAL_VALUE, compareFn, DEFAULT_KEY_PROP, GaFormlyUtil, trackByFunction } from '../ga-formly.util';
import { FormattingService } from '../../services/formatting.service';
import { util } from '../../util';
import { ExtraFieldType } from '../extra-field-type';
import * as _ from 'underscore';

@Component({
  selector: 'formly-field-select2',
  template: `
    <select
      class="form-control"
      [ngClass]="classNames"
      [attr.x-autocompletetype]="to.autoCompleteType"
      [attr.autocompletetype]="to.autoCompleteType"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [compareWith]="to.compareWith"
      [inputFocus]="to.inputFocus" #select>

      <option [ngValue]="undefined" *ngIf="to.showEmptyOption"></option>
      <ng-container *ngIf="to.options | formlySelectOptions: field | async as opts">
        <ng-container *ngIf="to._flatOptions; else grouplist">
          <ng-container *ngFor="let opt of opts; trackBy: trackByFunction">
            <ng-container *ngIf="!this.hasValueProp">
              <option [ngValue]="opt.value" [disabled]="opt.disabled" [attr.${ATTR_REAL_VALUE}]="setRealValue(opt.value)" [selected]="isSelectedControlValue(opt.value)">{{ opt.label | translate }}</option>
            </ng-container>
            <ng-container *ngIf="this.hasValueProp">
              <option [value]="opt.value" [disabled]="opt.disabled" [attr.${ATTR_REAL_VALUE}]="setRealValue(opt.value)" [selected]="isSelectedControlValue(opt.value)">{{ opt.label | translate }}</option>
            </ng-container>
          </ng-container>
        </ng-container>

        <ng-template #grouplist>
          <ng-container *ngFor="let opt of opts; trackBy: trackByFunction">
            <ng-container *ngIf="!this.hasValueProp">
              <option *ngIf="!opt.group; else optgroup" [ngValue]="opt.value" [disabled]="opt.disabled" [attr.${ATTR_REAL_VALUE}]="setRealValue(opt.value)" [selected]="isSelectedControlValue(opt.value)">{{ opt.label | translate }}</option>  
            </ng-container>
            <ng-container *ngIf="this.hasValueProp">
              <option *ngIf="!opt.group; else optgroup" [value]="opt.value" [disabled]="opt.disabled" [attr.${ATTR_REAL_VALUE}]="setRealValue(opt.value)" [selected]="isSelectedControlValue(opt.value)">{{ opt.label | translate }}</option>
            </ng-container>
            
            <ng-template #optgroup>
              <optgroup [label]="opt.label">
                <ng-container *ngIf="!this.hasValueProp">
                  <option *ngFor="let child of opt.group; trackBy: trackByFunction" [ngValue]="child.value" [disabled]="child.disabled" [attr.${ATTR_REAL_VALUE}]="setRealValue(child.value)" [selected]="isSelectedControlValue(child.value)">
                    {{ child.label | translate }}
                  </option>  
                </ng-container>
                <ng-container *ngIf="this.hasValueProp">
                  <option *ngFor="let child of opt.group; trackBy: trackByFunction" [value]="child.value" [disabled]="child.disabled" [attr.${ATTR_REAL_VALUE}]="setRealValue(child.value)" [selected]="isSelectedControlValue(child.value)">
                    {{ child.label | translate }}
                  </option>
                </ng-container>
              </optgroup>
            </ng-template>
          </ng-container>
        </ng-template>
      </ng-container>
    </select>
  `
})
export class FormlyFieldSelect2Component extends ExtraFieldType implements OnInit, AfterViewInit, AfterViewChecked {
  private select2Options:Select2.Options;
  public defaultOptions = {
    templateOptions: {
      options: [],
      compareWith(o1:any, o2:any):any {
        return o1?.id === o2?.id;
      }
    }
  };

  public trackByFunction:TrackByFunction<any>;
  private jSelect:JQuery<HTMLSelectElement>;
  private predicate: string;

  @ViewChild('select', { static: true }) private selectElementRef:ElementRef;

  constructor(
    private formattingService:FormattingService,
    private formlyUtil: GaFormlyUtil,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit():void {
    // set predicate of this field config
    const valueProp: string = _.isString(this.to.valueProp) ? this.to.valueProp : undefined;
    this.predicate = valueProp || this.to.trackBy || DEFAULT_KEY_PROP;
    this.to.compareWith = compareFn.bind(this);
    this.trackByFunction = trackByFunction.bind(this);
    this.formlyUtil.initSelectField(this.to, this.formControl);
    this.classNames = this.formlyUtil.createClassNames(FormlyType.SELECT2, <string>this.key, this.to.label);
    this.jSelect = $(this.selectElementRef.nativeElement);

    // event initial value, options and model change
    this.formlyUtil.onOptionsAndModelChange(this.jSelect, this.to, this.formControl);
  }

  public ngAfterViewInit():void {
    this.select2Init();

    // register listener
    this.listen();
  }

  public ngAfterViewChecked():void {
    this.changeDetectorRef.detectChanges();
  }

  protected listen():void {
    super.listen();

    // get common listener for select field
    this.formlyUtil.getSelectListener(this.to, this.formControl, this.jSelect);
    this.formlyUtil.getSelect2Listener(this.formControl, this.jSelect, false);
  }

  public isSelectedControlValue(value:any):boolean {
    if (util.isNullOrUndefinedOrEmpty(this.formControl.value)) {
      return false;
    }

    return _.isObject(this.formControl?.value) ? this.formControl?.value[this.predicate] === value[this.predicate] : this.formControl?.value === value;
  }

  public get hasValueProp(): boolean {
    return _.isString(this.to?.valueProp);
  }

  public setRealValue(value:any):any {
    return util.toJson(value);
  }

  private select2Init():void {
    this.select2Options = {
      allowClear: this.to.allowClear,
      placeholder: this.to.hidePlaceholder ? '' : (this.to.placeholder || this.to.label),
      escapeMarkup: undefined,
      templateResult: undefined,
      width: 'resolve'
    };

    if (this.to.parseHtml) {
      this.select2Options.templateResult = (data):string => <any>$.parseHTML(data.text);
      this.select2Options.escapeMarkup = (html:string) => {
        return <any>$.parseHTML(this.formattingService.removeHtmlBreaks(html, ', '));
      };
    }

    this.jSelect.select2(this.select2Options);
  }
}